import { useState, useEffect } from 'react';

/**
 * Hook para hacer debounce de un valor
 * Útil para búsquedas donde no queremos hacer una petición por cada tecla
 *
 * @param value - El valor a hacer debounce
 * @param delay - El tiempo de espera en milisegundos (default: 300ms)
 * @returns El valor con debounce aplicado
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState('')
 * const debouncedSearch = useDebouncedValue(search, 300)
 *
 * // Usar debouncedSearch para hacer la petición
 * useQuery({
 *   queryKey: ['search', debouncedSearch],
 *   queryFn: () => fetchData(debouncedSearch),
 * })
 * ```
 */
export function useDebouncedValue<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Crear un timeout para actualizar el valor después del delay
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Limpiar el timeout si el valor cambia antes de que se cumpla el delay
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook para crear una función con debounce
 * Útil cuando necesitas el callback en lugar del valor
 *
 * @param callback - La función a ejecutar con debounce
 * @param delay - El tiempo de espera en milisegundos (default: 300ms)
 * @returns La función con debounce aplicado
 */
export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number = 300,
): T {
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const debouncedCallback = ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = setTimeout(() => {
      callback(...args);
    }, delay);

    setTimeoutId(newTimeoutId);
  }) as T;

  // Limpiar el timeout al desmontar
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
}
